import { useQuery } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'

export const RANGE_OPTIONS = [7, 14, 30, 60, 90] as const
export type RangeDays = (typeof RANGE_OPTIONS)[number]

/**
 * The range the Activity card opens on for a user who has not chosen one.
 *
 * 30 rather than 7 because a week is short enough to be empty for someone who
 * hunted last month, and an all-zero card is indistinguishable from broken data —
 * a reading that has already cost review time once. 30 days is the shortest range
 * that usually contains something for an active account.
 *
 * The API's own fallback (ActivityEndpoints) is kept at the same number
 * deliberately: a request without `days` should describe the same window the UI
 * shows, so the two can never disagree about what "default" means.
 */
export const DEFAULT_RANGE: RangeDays = 30

interface RecentActivityResponse {
  bars: number[]
  barLabels: string[]
  highlightIndex: number
  gameReported: number
  hoursInField: number
  mapPins: number
  participants: number
  days: number
  windowStart: string
  windowEnd: string
}

interface ChronicleOverviewData {
  activity: {
    bars: number[]
    barLabels: string[]
    highlightIndex: number
    gameReported: number
    hoursInField: string
    mapPins: number
    participants: number
    days: number
  }
}

const PLACEHOLDER_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function emptyFor(days: number): ChronicleOverviewData {
  return {
    activity: {
      bars: [0, 0, 0, 0, 0, 0, 0],
      barLabels: PLACEHOLDER_LABELS,
      highlightIndex: -1,
      gameReported: 0,
      hoursInField: '0h',
      mapPins: 0,
      participants: 0,
      days,
    },
  }
}

function toView(resp: RecentActivityResponse): ChronicleOverviewData {
  // Backend returns exactly 7 entries, but guard against shape drift.
  const bars = (resp.bars ?? []).slice(0, 7)
  while (bars.length < 7) bars.push(0)
  const barLabels = (resp.barLabels ?? []).slice(0, 7)
  while (barLabels.length < 7) barLabels.push('')
  return {
    activity: {
      bars,
      barLabels,
      highlightIndex: resp.highlightIndex,
      gameReported: resp.gameReported,
      hoursInField: `${resp.hoursInField}h`,
      mapPins: resp.mapPins,
      participants: resp.participants,
      days: resp.days,
    },
  }
}

export function useChronicleOverview(days: RangeDays = DEFAULT_RANGE): ChronicleOverviewData {
  const call = useApiFetch()
  const { data } = useQuery({
    queryKey: ['chronicle', 'overview', 'activity', days],
    queryFn: async () => {
      const resp = await call<RecentActivityResponse>(`/chronicle/me/activity?days=${days}`)
      return toView(resp)
    },
    staleTime: 60 * 1000,
  })
  return data ?? emptyFor(days)
}
